import locale from "@bonliva-crm/locale";
import { Box, InputLabel, makeStyles, Typography } from "@material-ui/core";
import { useEffect, useState } from "react";
import { components, createFilter, GroupBase, OptionsOrGroups } from "react-select5";
import AsyncSelect, { AsyncProps } from "react-select5/async";
import { SelectBadge } from "./components/select-badge";
import { CheckboxOptionComponent } from "./components/checkbox-option";
import { ClearIndicatorComponent } from "./components/clear-indicator";
import { DropdownIndicatorComponent } from "./components/dropdown-indicator";
import { MultiValueRemoveComponent } from "./components/multi-value-remove";
import { PlaceholderComponent } from "./components/placeholder";
import { useSelectStyle } from "./hooks/use-select-style";
import { MultiSelectCommonProps } from "./utils/multi-select-common-props";
import { MenuComponent } from "./components/menu";

export function MultiSelectAsync<
  Option,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  showDropDownIndicator = true,
  hidePlaceholderOnFocus = true,
  hideController = false,
  menuWidth,
  ...mainProps
}: MultiSelectCommonProps<Option, Group> & AsyncProps<Option, true, Group>) {
  const {
    components: customComponents,
    options: _options,
    inputLabel,
    styleProps,
    showBadge,
    showOptionDivider,
    dropDownIndicatorIcon,
    isClearable,
    onClear,
    styles,
    onChange,
    menuChildren,
    ...rest
  } = mainProps;
  const { selectStyle, selectTheme } = useSelectStyle<Option, true, Group>(styleProps);

  const [_selectedAmount, _setSelectedAmount] = useState(0);
  const selectedAmount =
    mainProps.value && Array.isArray(mainProps.value)
      ? mainProps.value.length
      : _selectedAmount;

  const classes = useStyles({ menuWidth });

  const [options, setOptions] = useState<OptionsOrGroups<Option, Group> | undefined>(
    _options
  );

  const _customComponents = { ...customComponents };
  if (hideController) {
    _customComponents.Control = () => null;
  }

  useEffect(() => {
    setOptions(_options);
  }, [_options]);

  const firstValue =
    Array.isArray(mainProps.value) &&
    mainProps.value[0] &&
    "label" in mainProps.value[0] &&
    mainProps.value[0].label;

  return (
    <Box className={classes.root}>
      {showBadge && (
        <SelectBadge
          selectedAmount={selectedAmount}
          text={firstValue ? (firstValue as string) : undefined}
        />
      )}
      {inputLabel && (
        <InputLabel>
          <Typography variant="body1" style={{ opacity: mainProps.isDisabled ? 0.5 : 1 }}>
            {inputLabel}
          </Typography>
        </InputLabel>
      )}
      <AsyncSelect<Option, true, Group>
        components={{
          Option: (p) =>
            mainProps.optionType === "checkbox" ? (
              CheckboxOptionComponent({
                ...p,
                showDivider: showOptionDivider
              })
            ) : (
              <components.Option {...p} />
            ),
          Placeholder: (p) => PlaceholderComponent({ ...p, hidePlaceholderOnFocus }),
          DropdownIndicator: (p) =>
            showDropDownIndicator
              ? DropdownIndicatorComponent({
                  ...p,
                  icon: dropDownIndicatorIcon
                })
              : null,
          IndicatorSeparator: null,
          ClearIndicator: (p) =>
            isClearable && onClear ? ClearIndicatorComponent({ ...p, onClear }) : <></>,
          MultiValueRemove: (p) => MultiValueRemoveComponent(p),
          Menu: MenuComponent,
          ..._customComponents
        }}
        theme={(theme) => ({
          ...theme,
          ...selectTheme
        })}
        closeMenuOnSelect={true}
        hideSelectedOptions={true}
        isClearable={true}
        backspaceRemovesValue={true}
        noOptionsMessage={() => locale.NoMoreSearchResults}
        loadingMessage={() => ""}
        isMulti={true}
        isSearchable={true}
        styles={selectStyle}
        filterOption={createFilter({ ignoreAccents: false })}
        onChange={(newValue, meta) => {
          onChange && onChange(newValue, meta);
          showBadge && _setSelectedAmount(newValue.length);
        }}
        options={options}
        onMenuOpen={() => {
          if (mainProps.sortOptionsOnMenuOpenFn) {
            options && setOptions(mainProps.sortOptionsOnMenuOpenFn(options.slice()));
          }
        }}
        // @ts-expect-error recommended way to pass component props in
        // https://react-select.com/components#defining-components
        menuChildren={menuChildren}
        {...rest}
      />
    </Box>
  );
}

type StyleProps = {
  menuWidth?: string;
};

const useStyles = makeStyles((theme) => ({
  root: (props: StyleProps) => ({
    display: "flex",
    flexDirection: "column",
    rowGap: "0.5rem",
    position: "relative",
    height: "100%",
    width: props.menuWidth
  })
}));
